<!--
@component
TeamMatches

Variant of the matches widget that renders a chronological window of
matches involving a specific country — a mix of completed, live and
upcoming — capped at `MAX_MATCHES`. Used when the embed is loaded
with a `?country=<slug>` query parameter.

The window is anchored on the team's next live/upcoming match so the
rail can horizontally scroll-snap to it on load (see `focusMatchId`
forwarded to `MatchRail`). A handful of recently played matches are
kept on the left of the window for context.

Renders nothing until the team is resolved against the Opta data so
the unfiltered list never flashes in. The parent decides whether to
show a loading spinner while `team` is null.
-->

<script lang="ts">
  import MatchRail from './MatchRail.svelte';
  import { appState } from '$lib/stores/index.svelte';
  import getContestedMatches from '$utils/getContestedMatches/index.js';
  import { isPlaying, isUpcoming } from '$utils/getMatchStats';
  import getTeams from '$utils/getTeams';
  import { getMaxMatches, getUpcomingAfterPivot } from './railWindow.svelte';
  import type { ContestedMatch } from '$lib/@types/opta';

  /**
   * Total number of matches to show in the rail (played + live +
   * upcoming, combined). Responsive — shared with `AllMatches` via
   * `./railWindow` so both variants stay in lockstep.
   */
  const MAX_MATCHES = $derived(getMaxMatches());

  /**
   * How many of the `MAX_MATCHES` slots are reserved for the pivot
   * (next live/upcoming match) + the upcoming matches that follow it.
   * The rest of the window is filled with already-played matches on
   * the left. Bias here is intentional — the rail should land with
   * upcoming fixtures in view by default, with just enough recent
   * results for context.
   */
  const UPCOMING_AFTER_PIVOT = $derived(getUpcomingAfterPivot());

  /**
   * Derived: how many already-played matches we'd ideally keep on the
   * left of the pivot. `+ 1` accounts for the pivot itself, so the
   * three figures always sum to `MAX_MATCHES`.
   */
  const PLAYED_BEFORE_PIVOT = $derived(MAX_MATCHES - UPCOMING_AFTER_PIVOT - 1);

  type Props = {
    /** Country slug from `?country=<slug>`. */
    countrySlug: string;
  };

  const { countrySlug }: Props = $props();

  const team = $derived.by(() => {
    if (!countrySlug) return null;
    const teams = getTeams(appState.optaData?.matches);
    return teams.getTeam(countrySlug);
  });

  const allContestedMatches = $derived<ContestedMatch[]>(
    getContestedMatches(appState.optaData)
  );

  /** Every match this team has played or will play, sorted by kickoff. */
  const allTeamMatches = $derived<ContestedMatch[]>(
    team ?
      [...allContestedMatches]
        .filter((m) =>
          m?.matchInfo?.contestant?.some(
            (c) =>
              c?.id === team.id ||
              c?.code === team.code ||
              c?.name === team.name
          )
        )
        .sort(
          (a, b) =>
            new Date(a.isoDate ?? 0).getTime() -
            new Date(b.isoDate ?? 0).getTime()
        )
    : []
  );

  /**
   * Index of the team's "pivot" match — the first live match, or
   * failing that the first upcoming match. `-1` when the team has no
   * remaining fixtures, in which case we just show the tail of their
   * played matches.
   */
  const pivotIndex = $derived(
    allTeamMatches.findIndex((m) => isPlaying(m) || isUpcoming(m))
  );

  /**
   * The `MAX_MATCHES`-sized window we actually hand to the rail.
   * When the pivot exists we keep up to `PLAYED_BEFORE_PIVOT` played
   * matches to its left and fill the rest with the pivot + upcoming
   * matches. When no pivot exists (tournament over for this team)
   * we just show the last `MAX_MATCHES` played matches.
   */
  const matches = $derived<ContestedMatch[]>(
    (() => {
      if (allTeamMatches.length === 0) return [];

      if (pivotIndex < 0) {
        return allTeamMatches.slice(-MAX_MATCHES);
      }

      const start = Math.max(0, pivotIndex - PLAYED_BEFORE_PIVOT);
      return allTeamMatches.slice(start, start + MAX_MATCHES);
    })()
  );

  /**
   * The id of the pivot match (if any) so `MatchRail` can scroll-snap
   * to it on mount. Resolved against the windowed `matches` list so
   * the rail doesn't have to know about the full schedule.
   */
  const focusMatchId = $derived<string | undefined>(
    pivotIndex >= 0 ? allTeamMatches[pivotIndex]?.matchInfo?.id : undefined
  );
</script>

{#if team}
  <MatchRail {matches} {team} {focusMatchId} />
{/if}
